import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge, Card, Spinner } from "@aigntx/ui";
import { endpoints } from "../api";
import type { BloxGuide, GuideIndexItem } from "../types";

/**
 * The in-product guide.
 *
 * Written for the person using the system, not the person who built it: the
 * contractor's engineer wants to know which form to raise and who will see it.
 * The tables come from the server, generated from the same definitions the app
 * runs on, so nothing here can describe a workflow the software does not follow.
 *
 * It follows the interface language — an Arabic reader gets Arabic form names,
 * Arabic routes and Arabic folder names, not an Arabic page wrapped around
 * English tables.
 */
export function GuidePage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.startsWith("ar") ? "ar" : "en";

  const [list, setList] = useState<GuideIndexItem[] | null>(null);
  const [selected, setSelected] = useState<string>("eng.projects");
  const [doc, setDoc] = useState<BloxGuide | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    endpoints
      .listGuides(lang)
      .then((rows) => {
        setList(rows);
        // Open something with a guide rather than an empty page.
        const first = rows.find((r) => r.hasGuide);
        if (first) setSelected((cur) => (rows.find((r) => r.key === cur)?.hasGuide ? cur : first.key));
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : t("common.loadFailed")));
  }, [lang, t]);

  useEffect(() => {
    let live = true;
    setDoc(null);
    endpoints
      .getGuide(selected, lang)
      .then((d) => { if (live) setDoc(d); })
      .catch((e: unknown) => {
        if (live) setError(e instanceof Error ? e.message : t("common.loadFailed"));
      });
    return () => { live = false; };
  }, [selected, lang, t]);

  if (error) return <p className="page__error">{error}</p>;
  if (!list) return <Spinner />;

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">{t("guide.title")}</h1>
          <p className="page__sub">{t("guide.subtitle")}</p>
        </div>
      </div>

      <div className="guide">
        <Card className="guide__nav">
          <ul className="guide__blox">
            {list.map((b) => (
              <li key={b.key}>
                <button
                  type="button"
                  className={`guide__blox-btn${b.key === selected ? " is-on" : ""}`}
                  disabled={!b.hasGuide}
                  onClick={() => setSelected(b.key)}
                >
                  <span className="guide__blox-name">{b.name}</span>
                  <span className="guide__blox-meta">
                    {b.hasGuide ? `v${b.version}` : t("guide.noGuide")}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Card>

        <Card className="guide__body">
          {!doc ? (
            <Spinner />
          ) : !doc.hasGuide ? (
            <div className="guide__empty">
              <h2>{doc.name}</h2>
              <p className="muted">{t("guide.notWritten")}</p>
            </div>
          ) : (
            <article>
              <header className="guide__head">
                <h2 className="guide__name">{doc.name}</h2>
                <Badge tone="accent">v{doc.version}</Badge>
              </header>
              <p className="guide__summary">{doc.summary}</p>

              <nav className="guide__toc" aria-label={t("guide.contents")}>
                {doc.sections.map((s) => (
                  <a key={s.id} href={`#g-${s.id}`} className="guide__toc-link">
                    {s.title}
                  </a>
                ))}
              </nav>

              {doc.sections.map((s) => (
                <section key={s.id} id={`g-${s.id}`} className="guide__section">
                  <h3 className="guide__h">{s.title}</h3>
                  {s.body?.map((p, i) => (
                    <p key={i} className="guide__p">{p}</p>
                  ))}
                  {s.list && (
                    <dl className="guide__dl">
                      {s.list.map((item, i) => (
                        <div key={i} className="guide__dl-row">
                          <dt>{item.term}</dt>
                          <dd>{item.def}</dd>
                        </div>
                      ))}
                    </dl>
                  )}
                  {s.table && (
                    <div className="guide__tablewrap">
                      <table className="guide__table">
                        <thead>
                          <tr>
                            {s.table.columns.map((c, i) => (
                              <th key={i}>{c}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {s.table.rows.map((row, i) => (
                            <tr key={i}>
                              {s.table!.keys.map((k) => (
                                <td key={k}>{row[k] ?? ""}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </section>
              ))}
            </article>
          )}
        </Card>
      </div>
    </div>
  );
}
